import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { ChevronDown } from "lucide-react";

// WhatsApp business number - update this with your actual number
const WHATSAPP_NUMBER = "94771234567"; // Format: country code + number without + or spaces
const WHATSAPP_MESSAGE = "Hi! I'm interested in your 3D Night Lamps.";

const navItems = [
  { label: "HOME", to: "/", enabled: true },
  // Products will be inserted here dynamically
  { label: "BLOG", to: "/blog", enabled: true },
  { label: "ABOUT", to: "/our-story", enabled: true },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(
          collection(db, 'products'),
          where('status', '==', 'published'),
          orderBy('sortOrder', 'asc')
        );
        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productsData);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchProducts();
  }, []);

  const headerTop = 0;

  return (
    <header
      className="sticky top-0 left-0 right-0 z-50 bg-white shadow-md transition-all duration-300"
    >
      <div className="max-w-screen-xl mx-auto px-3 sm:px-4 md:px-6">
        <div className="flex items-center justify-between py-3 md:py-4 lg:py-5">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img
              src="/images/logo.png"
              alt="Merch Masters LK Logo"
              className="h-6 sm:h-7 md:h-8 lg:h-10 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span
              className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold uppercase tracking-[0.1em] sm:tracking-[0.15em] text-gray-900"
            >
              Merch Masters LK
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm uppercase tracking-[0.15em] font-medium">
            <Link
              to="/"
              className="text-gray-700 hover:text-[#E85D44] transition-colors"
            >
              HOME
            </Link>

            {/* Dynamic Products Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsProductsMenuOpen(true)}
              onMouseLeave={() => setIsProductsMenuOpen(false)}
            >
              <Link
                to="/products"
                className="flex items-center gap-1 text-gray-700 hover:text-[#E85D44] transition-colors"
              >
                PRODUCTS
                <ChevronDown className="w-4 h-4" />
              </Link>

              <div className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl overflow-hidden transition-all duration-300 transform origin-top-left ${isProductsMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'
                }`}>
                {products.length > 0 ? (
                  <div className="py-2">
                    {products.map((product) => (
                      <Link
                        key={product.id}
                        to={`/products/${product.slug}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsProductsMenuOpen(false)}
                      >
                        <div className="w-10 h-10 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                          {product.primaryImage ? (
                            <img src={product.primaryImage.url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">📷</div>
                          )}
                        </div>
                        <span className="text-gray-900 font-medium text-xs tracking-normal normal-case">
                          {product.name}
                        </span>
                      </Link>
                    ))}
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <Link
                        to="/products"
                        className="block px-4 py-2 text-center text-xs font-bold text-[#E85D44] hover:bg-gray-50 transition-colors uppercase tracking-wider"
                        onClick={() => setIsProductsMenuOpen(false)}
                      >
                        View All Products
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 text-gray-500 text-xs normal-case tracking-normal">
                    No products available
                  </div>
                )}
              </div>
            </div>

            <Link
              to="/blog"
              className="text-gray-700 hover:text-[#E85D44] transition-colors"
            >
              BLOG
            </Link>

            <Link
              to="/our-story"
              className="text-gray-700 hover:text-[#E85D44] transition-colors"
            >
              ABOUT
            </Link>



            {/* WhatsApp Button */}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 transition-colors text-gray-700 hover:text-[#25D366]"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp
            </a>

            {/* Resellers Button */}
            <Link
              to="/login"
              className="text-gray-700 hover:text-[#E85D44] transition-colors"
            >
              Resellers
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-gray-900"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col gap-4">
              <Link
                to="/"
                className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-medium transition-colors text-gray-700 hover:text-[#E85D44] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                HOME
              </Link>

              {/* Mobile Products List */}
              <div className="px-4 py-2">
                <span className="text-sm uppercase tracking-[0.15em] font-medium block mb-2 text-gray-900">
                  PRODUCTS
                </span>
                <div className="pl-4 space-y-2">
                  {products.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.slug}`}
                      className="block text-sm transition-colors text-gray-600 hover:text-[#E85D44]"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {product.name}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/blog"
                className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-medium transition-colors text-gray-700 hover:text-[#E85D44] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                BLOG
              </Link>

              <Link
                to="/our-story"
                className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-medium transition-colors text-gray-700 hover:text-[#E85D44] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ABOUT
              </Link>



              {/* WhatsApp Button - Mobile */}
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-sm uppercase tracking-[0.15em] font-medium transition-colors text-gray-700 hover:text-[#25D366] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>

              {/* Resellers Button - Mobile */}
              <Link
                to="/login"
                className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-medium transition-colors text-gray-700 hover:text-[#E85D44] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Resellers
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
